import { apiClient } from '../utils/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

class SharingService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/sharing`;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.eventListeners = new Map(); // For real-time updates
  }

  /**
   * Retry wrapper for network requests
   */
  async withRetry(operation, retries = this.maxRetries) {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0 && this.isRetryableError(error)) {
        await this.delay(this.retryDelay);
        return this.withRetry(operation, retries - 1);
      }
      throw this.handleError(error);
    }
  }

  /**
   * Check if error is retryable (network issues, 5xx errors)
   */
  isRetryableError(error) {
    if (!error.response) return true; // Network error
    const status = error.response.status;
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Delay utility for retry logic
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Enhanced error handling with user-friendly messages
   */
  handleError(error) {
    const errorMap = {
      400: 'Invalid data provided. Please check your input.',
      401: 'Authentication required. Please log in again.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      409: 'This child is already shared with this user.',
      422: 'Validation failed. Please check your input.',
      429: 'Too many requests. Please try again later.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable. Please try again.',
      503: 'Service temporarily unavailable. Please try again.',
    };

    if (!error.response) {
      return new Error('Network error. Please check your internet connection.');
    }

    const status = error.response.status;
    const message = errorMap[status] || 'An unexpected error occurred.';
    
    const enhancedError = new Error(message);
    enhancedError.originalError = error;
    enhancedError.status = status;
    enhancedError.data = error.data;
    
    return enhancedError;
  }

  /**
   * Subscribe to real-time sharing updates
   */
  subscribe(eventType, callback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType).add(callback);
    
    return () => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Emit real-time updates to subscribers
   */
  emit(eventType, data) {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  /**
   * Get all shares for current user (owned and received)
   */
  async getAllShares() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Get shared users for a specific child
   */
  async getSharedUsers(childId) {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/?child=${childId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Invite user to share child access with validation
   */
  async inviteUser(childId, inviteData) {
    return this.withRetry(async () => {
      // Validate invite data
      const validatedData = this.validateInviteData(inviteData);
      
      const response = await apiClient.post(`${this.baseURL}/shares/invite_user/`, {
        child_id: childId,
        email: validatedData.email,
        permission_level: validatedData.permissionLevel,
        message: validatedData.message || '',
        expires_at: validatedData.expiresAt,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Emit real-time update
      this.emit('invitation_sent', {
        childId,
        invitation: result,
      });
      
      return result;
    });
  }

  /**
   * Update user permissions for a share
   */
  async updateUserPermissions(shareId, permissionLevel) {
    return this.withRetry(async () => {
      const response = await apiClient.patch(
        `${this.baseURL}/shares/${shareId}/update_permission/`,
        { permission_level: permissionLevel }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Emit real-time update
      this.emit('permissions_updated', {
        shareId,
        permissionLevel,
        share: result,
      });
      
      return result;
    });
  }

  /**
   * Remove user access (revoke share)
   */
  async removeUserAccess(shareId) {
    return this.withRetry(async () => {
      const response = await apiClient.post(`${this.baseURL}/shares/${shareId}/revoke_access/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Emit real-time update
      this.emit('access_revoked', {
        shareId,
      });
      
      return { success: true };
    });
  }

  /**
   * Get pending invitations (sent by current user)
   */
  async getPendingInvitations(childId = null) {
    return this.withRetry(async () => {
      let url = `${this.baseURL}/shares/`;
      const params = new URLSearchParams({ status: 'pending' });
      if (childId) {
        params.append('child', childId);
      }
      url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results || data; // Handle paginated response
    });
  }

  /**
   * Get received invitations (for current user)
   */
  async getReceivedInvitations() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/received_invitations/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results || data; // Handle paginated response or direct array
    });
  }

  /**
   * Accept invitation by token
   */
  async acceptInvitation(token) {
    return this.withRetry(async () => {
      const response = await apiClient.post(
        `${this.baseURL}/invitations/${token}/accept/`,
        {}
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      this.emit('invitation_accepted', { token, share: result });

      return result;
    });
  }

  /**
   * Decline invitation by token
   */
  async declineInvitation(token, feedback = null) {
    return this.withRetry(async () => {
      const response = await apiClient.post(
        `${this.baseURL}/invitations/${token}/decline/`,
        { feedback }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      this.emit('invitation_declined', { token, share: result });

      return result;
    });
  }

  /**
   * Cancel/withdraw invitation
   */
  async cancelInvitation(shareId) {
    return this.withRetry(async () => {
      const response = await apiClient.delete(`${this.baseURL}/shares/${shareId}/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Emit real-time update
      this.emit('invitation_cancelled', {
        shareId,
      });
      
      return { success: true };
    });
  }

  /**
   * Resend invitation
   */
  async resendInvitation(shareId) {
    return this.withRetry(async () => {
      const response = await apiClient.post(`${this.baseURL}/shares/${shareId}/resend/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Emit real-time update
      this.emit('invitation_resent', {
        shareId,
        share: result,
      });
      
      return result;
    });
  }

  /**
   * Get user permissions for a specific child
   */
  async getUserPermissions(childId) {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/permissions/?child=${childId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Get sharing activity/audit log
   */
  async getSharingActivity(childId = null, params = {}) {
    return this.withRetry(async () => {
      let url = `${this.baseURL}/audit-logs/`;
      const queryParams = new URLSearchParams(params);
      
      if (childId) {
        queryParams.append('child', childId);
      }
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
      
      const response = await apiClient.get(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Get data access logs
   */
  async getDataAccessLogs(childId = null, params = {}) {
    return this.withRetry(async () => {
      let url = `${this.baseURL}/access-logs/`;
      const queryParams = new URLSearchParams(params);
      
      if (childId) {
        queryParams.append('child', childId);
      }
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
      
      const response = await apiClient.get(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Bulk operations for multiple invitations
   */
  async bulkInviteUsers(childId, invitations) {
    const results = [];
    const errors = [];
    
    for (const invitation of invitations) {
      try {
        const result = await this.inviteUser(childId, invitation);
        results.push(result);
      } catch (error) {
        errors.push({
          email: invitation.email,
          error: error.message,
        });
      }
    }
    
    return {
      successful: results,
      failed: errors,
      total: invitations.length,
    };
  }

  /**
   * Validate invitation data
   */
  validateInviteData(data) {
    const errors = {};
    
    if (!data.email || !this.isValidEmail(data.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    
    if (!data.permissionLevel || !this.getPermissionLevels().some(p => p.value === data.permissionLevel)) {
      errors.permissionLevel = 'Please select a valid permission level.';
    }
    
    if (data.expiresAt && new Date(data.expiresAt) <= new Date()) {
      errors.expiresAt = 'Expiration date must be in the future.';
    }
    
    if (Object.keys(errors).length > 0) {
      const error = new Error('Validation failed');
      error.validationErrors = errors;
      throw error;
    }
    
    return {
      ...data,
      email: data.email.toLowerCase().trim(),
    };
  }

  /**
   * Email validation utility
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get available permission levels
   */
  getPermissionLevels() {
    // Must match backend PERMISSION_CHOICES in ChildShare model
    return [
      {
        value: 'view',
        label: 'View Only',
        description: 'Can view all data but cannot make changes',
        icon: 'eye',
      },
      {
        value: 'edit',
        label: 'Full Access',
        description: 'Can view, add, edit, and delete entries',
        icon: 'edit',
      }
    ];
  }

  /**
   * Get shares owned by current user
   */
  async getOwnedShares() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results || data; // Handle paginated response
    });
  }

  /**
   * Get shares received by current user (accepted shares)
   */
  async getReceivedShares() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/shared_children/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results || data; // Handle paginated response or direct array
    });
  }

  /**
   * Get shared children (children shared with current user)
   */
  async getSharedChildren() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/shares/shared_children/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results || data; // Handle paginated response or direct array
    });
  }

  /**
   * Get invitation details by token
   */
  async getInvitationByToken(token) {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/invitations/${token}/`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  /**
   * Accept invitation by token with optional feedback
   */
  async acceptInvitationByToken(token, feedback = null, registrationData = null) {
    return this.withRetry(async () => {
      const payload = { feedback, accepted_at: new Date().toISOString() };
      if (registrationData) {
        Object.assign(payload, registrationData);
      }
      const response = await apiClient.post(
        `${this.baseURL}/invitations/${token}/accept/`,
        payload
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Emit real-time update
      this.emit('invitation_accepted', {
        token,
        share: result,
      });
      
      return result;
    });
  }

  /**
   * Decline invitation by token with optional feedback
   */
  async declineInvitationByToken(token, feedback = null) {
    return this.withRetry(async () => {
      const response = await apiClient.post(`${this.baseURL}/invitations/${token}/decline/`, {
        feedback,
        declined_at: new Date().toISOString(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Emit real-time update
      this.emit('invitation_declined', {
        token,
        share: result,
      });
      
      return result;
    });
  }

  /**
   * Get sharing statistics
   */
  async getSharingStats() {
    return this.withRetry(async () => {
      const [allShares, pendingInvitations, receivedShares] = await Promise.all([
        this.getAllShares(),
        this.getPendingInvitations(),
        this.getReceivedInvitations(),
      ]);
      
      return {
        totalShares: allShares.length,
        activeShares: allShares.filter(s => s.status === 'accepted').length,
        pendingInvitations: pendingInvitations.length,
        receivedShares: receivedShares.length,
        childrenShared: [...new Set(allShares.map(s => s.child))].length,
      };
    });
  }
}

// Create and export singleton instance
const sharingServiceInstance = new SharingService();

// Legacy export for backward compatibility
export const sharingService = sharingServiceInstance;
export default sharingServiceInstance;